import { useCallback, useEffect, useState } from 'react';
import { api, RequestFailed, type Me, type Role } from './api';
import { Banner, Boundary, Card, Loading } from './ui';
import { IndriveIcon, IndriveWordmark } from './brand';
import { Overview } from './screens/Overview';
import { Referrals } from './screens/Referrals';
import { ReferralDetail } from './screens/ReferralDetail';
import { Drivers } from './screens/Drivers';
import { DriverDetail } from './screens/DriverDetail';
import { Joined } from './screens/Joined';
import { Flagged } from './screens/Flagged';
import { Float } from './screens/Float';
import { Batches } from './screens/Batches';
import { Ops } from './screens/Ops';

type Route =
  | { name: 'overview' } | { name: 'referrals' } | { name: 'referral'; id: string }
  | { name: 'drivers' } | { name: 'driver'; id: string } | { name: 'joined' }
  | { name: 'flagged' } | { name: 'float' } | { name: 'batches' } | { name: 'ops' };

const TITLES: Record<Route['name'], { title: string; sub: string }> = {
  overview:  { title: 'Overview', sub: 'Programme health at a glance' },
  referrals: { title: 'Referrals', sub: 'Every referral inDrive has submitted' },
  referral:  { title: 'Referral', sub: 'Checks, payout attempts and full audit history' },
  drivers:   { title: 'Drivers', sub: 'Who is bringing drivers in, and what the programme has paid them' },
  driver:    { title: 'Driver', sub: 'Identity, KYC evidence, referral network and connections' },
  joined:    { title: 'Joined drivers', sub: 'Drivers who signed up through the join link. Nothing is checked until they qualify' },
  flagged:   { title: 'Review queue', sub: 'Referrals a human must decide on before any money moves' },
  float:     { title: 'Float', sub: "inDrive's pre-funded balance and its ledger" },
  batches:   { title: 'Bulk upload', sub: 'Submit referrals by CSV' },
  ops:       { title: 'Operations', sub: 'Reconciliation, stuck work and undelivered events' },
};

/** Which roles may see which sections. Enforced again server-side. */
const VISIBLE: Record<Route['name'], Role[] | null> = {
  overview: null,
  referrals: null,
  referral: null,
  drivers: null,
  driver: null,
  joined: ['indrive_ops', 'indrive_admin', 'zeeh_admin'],
  flagged: ['reviewer', 'zeeh_admin', 'indrive_admin'],
  float: null,
  batches: ['indrive_ops', 'indrive_admin', 'zeeh_admin'],
  ops: ['zeeh_admin', 'indrive_admin'],
};

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [booting, setBooting] = useState(true);
  const [route, setRoute] = useState<Route>(parseHash());
  const [flaggedCount, setFlaggedCount] = useState<number | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>(
    () => (localStorage.getItem('zeeh-theme') as 'light' | 'dark' | null)
      ?? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'),
  );

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('zeeh-theme', theme);
  }, [theme]);

  useEffect(() => {
    api.me().then(setMe).catch(() => setMe(null)).finally(() => setBooting(false));
  }, []);

  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Keep the review-queue badge honest without polling the whole overview.
  useEffect(() => {
    if (!me) return;
    if (!canSee('flagged', me.user.role)) return;
    api.flagged().then((f) => setFlaggedCount(f.referrals.length)).catch(() => undefined);
  }, [me, route]);

  const go = useCallback((r: string) => { window.location.hash = r; }, []);

  // On a phone the sidebar is a drawer. Any navigation closes it — leaving it
  // open over the screen you just asked for is the classic mobile-nav bug.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { setNavOpen(false); }, [route]);
  useEffect(() => {
    if (!navOpen) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [navOpen]);

  if (booting) return <Loading label="Starting" />;
  if (!me) return <Login onSignedIn={setMe} />;

  const meta = TITLES[route.name];
  const role = me.user.role;

  return (
    <div className={`shell${navOpen ? ' nav-open' : ''}`}>
      <div className="scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />

      <aside className="sidebar" id="sidebar">
        <div className="brand">
          <div className="brand-mark">
            <IndriveIcon size={30} />
            <IndriveWordmark height={17} />
          </div>
          <div className="brand-sub">Referral payouts</div>
        </div>

        <nav className="nav">
          <div className="nav-label">Programme</div>
          <NavItem route={route} name="overview" label="Overview" go={go} />
          <NavItem route={route} name="referrals" label="Referrals" go={go} />
          <NavItem route={route} name="drivers" label="Drivers" go={go} />
          {canSee('joined', role) && <NavItem route={route} name="joined" label="Joined drivers" go={go} />}
          {canSee('flagged', role) && (
            <NavItem route={route} name="flagged" label="Review queue" go={go} count={flaggedCount ?? undefined} alert={(flaggedCount ?? 0) > 0} />
          )}

          <div className="nav-label">Money</div>
          <NavItem route={route} name="float" label="Float &amp; ledger" go={go} />
          {canSee('batches', role) && <NavItem route={route} name="batches" label="Bulk upload" go={go} />}
          {canSee('ops', role) && <NavItem route={route} name="ops" label="Operations" go={go} />}
        </nav>

        <div className="sidebar-foot">
          {me.adapters === 'mock' && (
            <div className="small" style={{ marginBottom: 10, color: 'var(--info)' }}>
              Mock adapters — no real money moves
            </div>
          )}
          <div className="row">
            <div className="who">
              <strong>{me.user.name}</strong>
              <span>{ROLE_LABEL[role]}</span>
            </div>
            <div style={{ flex: 1 }} />
            <button
              className="btn ghost sm"
              title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
              aria-label="Toggle colour scheme"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              {theme === 'dark' ? '☾' : '☀'}
            </button>
          </div>
          <button
            className="btn ghost sm"
            style={{ marginTop: 8, width: '100%' }}
            onClick={async () => { await api.logout(); setMe(null); }}
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button
            className="nav-toggle"
            aria-label="Menu"
            aria-expanded={navOpen}
            aria-controls="sidebar"
            onClick={() => setNavOpen((v) => !v)}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M2.5 5h15M2.5 10h15M2.5 15h15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
          <div className="topbar-title">
            <h1>{meta.title}</h1>
            <div className="sub">{meta.sub}</div>
          </div>
        </header>

        <div className="content">
          <Boundary key={`${route.name}:${'id' in route ? route.id : ''}`} onReset={() => go('overview')}>
          {!canSee(route.name, role) ? (
            <Banner tone="warn" title="Not available to your role">
              Your role ({ROLE_LABEL[role]}) does not have access to this section.
            </Banner>
          ) : route.name === 'overview' ? <Overview go={go} />
            : route.name === 'referrals' ? <Referrals open={(id) => go(`referral/${id}`)} role={role} />
            : route.name === 'referral' ? <ReferralDetail id={route.id} role={role} back={() => go('referrals')} openDriver={(ref) => go(`driver/${ref}`)} />
            : route.name === 'drivers' ? <Drivers open={(ref) => go(`driver/${ref}`)} />
            : route.name === 'driver' ? (
                <DriverDetail
                  driverRef={route.id}
                  role={role}
                  back={() => go('drivers')}
                  openReferral={(id) => go(`referral/${id}`)}
                  openDriver={(ref) => go(`driver/${ref}`)}
                />
              )
            : route.name === 'joined' ? <Joined role={role} />
            : route.name === 'flagged' ? <Flagged />
            : route.name === 'float' ? <Float role={role} />
            : route.name === 'batches' ? <Batches role={role} />
            : <Ops />}
          </Boundary>
        </div>
      </main>
    </div>
  );
}

const ROLE_LABEL: Record<Role, string> = {
  zeeh_admin: 'Zeeh administrator',
  indrive_admin: 'inDrive administrator',
  indrive_ops: 'inDrive operations',
  reviewer: 'Compliance reviewer',
};

function canSee(name: Route['name'], role: Role): boolean {
  const allowed = VISIBLE[name];
  return allowed === null || allowed.includes(role);
}

function NavItem({ route, name, label, go, count, alert }: {
  route: Route; name: Route['name']; label: string; go: (r: string) => void; count?: number; alert?: boolean;
}) {
  const active =
    route.name === name ||
    (name === 'referrals' && route.name === 'referral') ||
    (name === 'drivers' && route.name === 'driver');
  return (
    <button
      className={`nav-item${alert ? ' alert' : ''}`}
      aria-current={active ? 'page' : undefined}
      onClick={() => go(name)}
    >
      {label.replace('&amp;', '&')}
      {count !== undefined && count > 0 && <span className="count">{count}</span>}
    </button>
  );
}

function parseHash(): Route {
  const h = window.location.hash.replace(/^#\/?/, '');
  // The hash carries a URL-encoded id (go() sets it via `location.hash =`,
  // which the browser encodes) — decode it back before it's used as a route
  // param, or a driver id containing anything non-alphanumeric (a space, in
  // practice) is re-encoded a second time downstream and never matches.
  if (h.startsWith('referral/')) return { name: 'referral', id: decodeURIComponent(h.slice('referral/'.length)) };
  if (h.startsWith('driver/')) return { name: 'driver', id: decodeURIComponent(h.slice('driver/'.length)) };
  const known: Route['name'][] = ['overview', 'referrals', 'drivers', 'joined', 'flagged', 'float', 'batches', 'ops'];
  const hit = known.find((k) => k === h);
  return hit ? ({ name: hit } as Route) : { name: 'overview' };
}

function Login({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(email.trim(), password);
      onSignedIn(await api.me());
    } catch (err) {
      setError(err instanceof RequestFailed ? err.body.message : (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <div className="login-card">
        <div className="login-mark">
          <div className="m">
            <IndriveIcon size={38} />
            <IndriveWordmark height={22} />
          </div>
          <div className="s">Referral payout engine</div>
        </div>

        <Card>
          <form onSubmit={submit}>
            {error && <Banner tone="danger">{error}</Banner>}
            <div className="field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required autoFocus />
            </div>
            <div className="field">
              <label htmlFor="pw">Password</label>
              <input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
            </div>
            <button className="btn primary" type="submit" disabled={busy} style={{ width: '100%', padding: '9px' }}>
              {busy ? <span className="spin" /> : 'Sign in'}
            </button>
          </form>
        </Card>

        {import.meta.env.DEV && (
          <div className="login-hint">
            Development accounts — password <b>zeeh-dev-password</b>
            <div style={{ marginTop: 6 }}>
              <b>admin@zeeh.africa</b> — full access<br />
              <b>admin@indrive.com</b> — inDrive administrator<br />
              <b>ops@indrive.com</b> — operations<br />
              <b>reviewer@zeeh.africa</b> — review queue only
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
